import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { withTheme } from '@rjsf/core'
import { Theme as ShadcnTheme } from '@rjsf/shadcn'
import { customizeValidator } from '@rjsf/validator-ajv8'
import { Upload } from 'lucide-react'
import { BackIcon } from '../components/icons'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import { useLanguage } from '../context/LanguageContext'
import { useToast } from '../context/ToastContext'
import InquiryService from '../services/inquiry.service'
import apiService from '../services/api.service'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'
import Sanitizer from '../utils/sanitizer'
import {
  ALLOWED_INQUIRY_MIME_TYPES,
  BLOCKED_EXECUTABLE_EXTENSIONS,
} from '../constants/fileValidation'
import Button from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Checkbox } from '../components/ui/checkbox'
import { Label } from '../components/ui/label'
import { CalendarDropdown } from '../components/ui/calender-dropdown'
import { Field, FieldLabel, FieldSet, FieldGroup } from '../components/ui/field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  MultiSelect,
  MultiSelectTrigger,
  MultiSelectValue,
  MultiSelectContent,
  MultiSelectItem,
} from '../components/ui/multi-select'
import Modal from '../components/ui/modal'
import Loader from '../components/ui/loader'
import NotFound from './NotFound'
import { isCustomerInquiryEnabled } from '../utils/tenantFeatures'
import Ajv from 'ajv'
import ajvErrors from 'ajv-errors'

const SPIN_CASHBACK_MIN_DATE = '2026-02-10'
const SPIN_CASHBACK_DATE_ERROR_MESSAGE =
  'Spin cashback is valid only for transactions from 10 February 2026 onward.'

const SPIN_CASHBACK_TYPE_VALUES = new Set([
  'Spin Cashback',
  'ルーレットキャッシュバック',
])
const CASHBACK_TYPE_VALUES = new Set(['Cashback', '通常キャッシュバック'])
const INQUIRY_MAPPING_NOT_MAPPED = 'Not mapped'
const SPIN_ONLY_CAMPAIGN_VALUES = new Set([
  '【9/1-10/15開催】Rakuten Card Exclusive: Get Up to ¥1,000 Cashback! Visa Touch Payment with Apple Pay Campaign',
  '【9/1-10/15開催】楽天カード限定最大1,000円キャッシュバック！Apple PayでVisaのタッチ決済！キャンペーン',
])
const CASHBACK_ONLY_CAMPAIGN_VALUES = new Set([
  '[Starts June 11] Save More with a Tap — Apple Pay Campaign Too! (Recovery campaign – former SMBC Finance Service cards)',
  '[Starts June 11] Save More with a Tap — Apple Pay Campaign Too! + [Starts June 11] Even More Savings at Participating Stores! (Recovery campaign – former SMBC Finance Service cards)',
  '6/11-7/19　Apple Payでも！タッチでVisa割キャンペーン！（旧SMBCファイナンスサービス株式会社発行カード）',
  '6/11-7/19　Apple Payでも！タッチでVisa割キャンペーン！さらに特定のお店でお得！（旧SMBCファイナンスサービス株式会社発行カード）',
])

const normalizeInquiryMappingLabel = (value) =>
  String(value || '')
    .replace(/\u3000/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const getInquirySchemaProperties = (schema, propertyName, matches = []) => {
  if (!schema) {
    return matches
  }

  if (schema.properties?.[propertyName]) {
    matches.push(schema.properties[propertyName])
  }

  const nestedSchemas = [
    ...(Array.isArray(schema.allOf) ? schema.allOf : []),
    ...(Array.isArray(schema.anyOf) ? schema.anyOf : []),
    ...(Array.isArray(schema.oneOf) ? schema.oneOf : []),
  ]

  nestedSchemas.forEach((nestedSchema) => {
    if (nestedSchema?.properties?.[propertyName]) {
      matches.push(nestedSchema.properties[propertyName])
    }

    getInquirySchemaProperties(nestedSchema?.then, propertyName, matches)
  })

  return matches
}

const getInquiryCampaignSchema = (schema) => {
  const campaignSchemas = getInquirySchemaProperties(schema, 'campaignName')
  const primarySchema = campaignSchemas[0] || null
  const mappings = []
  const seenMappingKeys = new Set()

  campaignSchemas.forEach((campaignSchema) => {
    const schemaMappings = Array.isArray(campaignSchema?.['x-idMappings'])
      ? campaignSchema['x-idMappings']
      : []

    schemaMappings.forEach((mapping) => {
      const mappingKey = [
        mapping.id,
        mapping.label,
        ...(Array.isArray(mapping.aliases) ? mapping.aliases : []),
      ]
        .map(normalizeInquiryMappingLabel)
        .join('|')

      if (!seenMappingKeys.has(mappingKey)) {
        seenMappingKeys.add(mappingKey)
        mappings.push(mapping)
      }
    })
  })

  return primarySchema
    ? {
        ...primarySchema,
        'x-idMappings': mappings,
      }
    : null
}

const findInquiryMappingEntry = (mappingEntries, selectedValue) => {
  const normalizedSelectedValue = normalizeInquiryMappingLabel(selectedValue)

  return mappingEntries.find((entry) => {
    const labels = [entry.label, ...(Array.isArray(entry.aliases) ? entry.aliases : [])]
    return labels.some(
      (label) => normalizeInquiryMappingLabel(label) === normalizedSelectedValue,
    )
  })
}

const formatInquiryCampaignDetails = (campaignNames, campaignSchema) => {
  const names = Array.isArray(campaignNames)
    ? campaignNames
    : campaignNames
      ? [campaignNames]
      : []
  const mappingEntries = Array.isArray(campaignSchema?.['x-idMappings'])
    ? campaignSchema['x-idMappings']
    : []
  const idLabel = campaignSchema?.['x-idLabel'] || 'ID'

  return names
    .filter(Boolean)
    .map((campaignName) => {
      const mappingEntry = findInquiryMappingEntry(mappingEntries, campaignName)
      const mappedId = mappingEntry?.id || INQUIRY_MAPPING_NOT_MAPPED
      return `${idLabel}: ${mappedId}`
    })
    .join('\n')
}

const getInquiryCampaignSelections = (campaignNames) => {
  if (Array.isArray(campaignNames)) {
    return campaignNames.filter(Boolean)
  }

  return campaignNames ? [campaignNames] : []
}

const isCashbackMissingInquiryForm = (formId) =>
  formId === 'cashbackMissingEmailReceived' ||
  formId === 'cashbackMissingEmailNotReceived'

const CAMPAIGN_FIRST_FORM_IDS = new Set([
  'cannotLogin',
  'cannotSignUp',
  'cannotAccessEmail',
  'cannotAccessRegisteredEmail',
  'cashbackAmountIncorrect',
  'pageNotLoading',
  'offersNotShowing',
  'redirectedToWrongPage',
  'cardMissing',
  'cannotAddCard',
  'cannotRegisterCard',
  'cannotAddCardAlreadyRegistered',
  'unknownCardRegistered',
  'changeEmail',
])

const buildInquirySubmissionMetadata = (formId, submittedData, schema) => {
  const metadata = {}
  const campaignSchema = getInquiryCampaignSchema(schema)

  if (isCashbackMissingInquiryForm(formId)) {
    const selections = Array.isArray(submittedData.requestToCheck)
      ? submittedData.requestToCheck
      : []
    const selectedTypes = []

    if (
      selections.some((selection) => CASHBACK_TYPE_VALUES.has(selection))
    ) {
      selectedTypes.push('Cashback')
    }

    if (
      selections.some((selection) => SPIN_CASHBACK_TYPE_VALUES.has(selection))
    ) {
      selectedTypes.push('Spin')
    }

    if (selectedTypes.length > 0) {
      metadata.selectedType = selectedTypes.join(', ')
    }

    const campaignSelections = getInquiryCampaignSelections(
      submittedData.campaignName,
    )
    if (campaignSelections.length > 0) {
      metadata.campaignNames = campaignSelections.join('\n')
    }

    const campaignOfferDetails = formatInquiryCampaignDetails(
      submittedData.campaignName,
      campaignSchema,
    )

    if (campaignOfferDetails) {
      metadata.campaignOfferDetails = campaignOfferDetails
    }
  }

  if (formId === 'spinMissing') {
    const spinCampaignDetails = formatInquiryCampaignDetails(
      submittedData.campaignName,
      campaignSchema,
    )

    if (spinCampaignDetails) {
      metadata.spinCampaignDetails = spinCampaignDetails
    }
  }

  if (
    formId !== 'spinMissing' &&
    !isCashbackMissingInquiryForm(formId)
  ) {
    const campaignOfferDetails = formatInquiryCampaignDetails(
      submittedData.campaignName,
      campaignSchema,
    )

    if (campaignOfferDetails) {
      metadata.campaignOfferDetails = campaignOfferDetails
    }
  }

  return metadata
}

const hasHtmlMarkup = (value) => /<\/?[a-z][\s\S]*>/i.test(value)

const escapeInquiryHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const decodeInquiryHtmlEntities = (value) => {
  const rawValue = String(value || '')

  if (typeof document === 'undefined') {
    return rawValue
  }

  const textarea = document.createElement('textarea')
  textarea.innerHTML = rawValue
  return textarea.value
}

const highlightCannotLoginPath = (value) => {
  const highlightedTexts = [
    '「Card Registration Related」→「I cannot register my card (it may already be registered to another account)」',
    '「カード登録関連」→「カードが登録できない（他のアカウントに登録されている可能性がある）」',
  ]

  return highlightedTexts.reduce((content, text) => {
    const escapedText = escapeInquiryHtml(text)
    return content.replaceAll(escapedText, `<strong>${escapedText}</strong>`)
  }, value)
}

const buildCannotLoginInfoHtml = (value) => {
  const rawValue = String(value || '').trim()
  const markers = [
    'If the account does not exist',
    'If the account is locked',
    'If you do not receive the password reset email',
    'If your card is registered to an account you used previously',
    'アカウントが存在しない場合',
    'アカウントがロックされた場合',
    'パスワード再設定メールが届かない場合',
    '過去にご利用されていたアカウントにカードが登録されている場合',
  ]
    .map((marker) => ({ marker, index: rawValue.indexOf(marker) }))
    .filter(({ index }) => index >= 0)
    .sort((a, b) => a.index - b.index)

  if (markers.length < 2) {
    return ''
  }

  const sections = markers.map(({ marker, index }, markerIndex) => {
    const nextIndex = markers[markerIndex + 1]?.index ?? rawValue.length
    return {
      title: marker,
      body: rawValue.slice(index + marker.length, nextIndex).trim(),
    }
  })

  return `<ul>${sections
    .map(({ title, body }) => {
      const bodyHtml = body
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => `<p>${highlightCannotLoginPath(escapeInquiryHtml(line))}</p>`)
        .join('')

      return `<li><p>${escapeInquiryHtml(title)}</p>${bodyHtml}</li>`
    })
    .join('')}</ul>`
}

const normalizeInquiryInfoHtml = (value, currentFormId) => {
  const rawValue = String(value || '').trim()
  const decodedValue = decodeInquiryHtmlEntities(rawValue).trim()

  if (!rawValue) {
    return ''
  }

  if (hasHtmlMarkup(decodedValue)) {
    return decodedValue
  }

  if (currentFormId === 'cannotLogin') {
    const cannotLoginInfoHtml = buildCannotLoginInfoHtml(decodedValue || rawValue)
    if (cannotLoginInfoHtml) {
      return cannotLoginInfoHtml
    }
  }

  const textValue = decodedValue || rawValue

  const formatInlineText = (line) =>
    escapeInquiryHtml(line).replace(
      /\*\*(.*?)\*\*/g,
      '<strong>$1</strong>',
    )

  const formatTextLine = (line) => {
    const markdownHeading = line.match(/^###\s+(.+)$/)
    const japaneseBracketHeading = line.match(/^【.+】$/)

    if (markdownHeading || japaneseBracketHeading) {
      return `<h3>${formatInlineText(markdownHeading?.[1] || line)}</h3>`
    }

    return `<p>${formatInlineText(line)}</p>`
  }

  if (textValue.includes('◆') || textValue.includes('♦')) {
    const bulletSections = textValue
      .split(/[◆♦]/)
      .map((section) => section.trim())
      .filter(Boolean)

    if (bulletSections.length > 0) {
      return `<ul>${bulletSections
        .map((section) => `<li>${escapeInquiryHtml(section)}</li>`)
        .join('')}</ul>`
    }
  }

  return textValue
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(formatTextLine)
    .join('')
}

const parseDateOnly = (dateString) => {
  if (!dateString) return null
  const parts = dateString.split('-').map(Number)
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return null
  }

  const [year, month, day] = parts
  const date = new Date(year, month - 1, day)
  date.setHours(0, 0, 0, 0)
  return date
}

// Create Ajv instance with required options
const ajv = new Ajv({
  allErrors: true,
  verbose: true,
  $data: true, // Required for ajv-errors
  strict: false,
  strictSchema: false,
})

// Apply ajv-errors to enable errorMessage support
ajvErrors(ajv)

// Create custom validator
const customValidator = customizeValidator({
  ajvOptionsOverrides: {
    allErrors: true,
    verbose: true,
    $data: true,
    strict: false,
    strictSchema: false,
  },
  ajvInstance: ajv,
  customFormats: {
    'date-not-future': {
      validate: (dateString) => {
        const date = parseDateOnly(dateString)
        if (!date) return false
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        return date <= today
      },
    },
    'date-not-future-spin': {
      validate: (dateString) => {
        const date = parseDateOnly(dateString)
        const minDate = parseDateOnly(SPIN_CASHBACK_MIN_DATE)

        if (!date || !minDate) return false

        const today = new Date()
        today.setHours(23, 59, 59, 999)

        return date >= minDate && date <= today
      },
    },
  },
})

// Store the form schema at module level so transformErrors can access it
let currentFormSchema = null

// Transform errors to use custom error messages from schema
const transformErrors = (errors) => {
  if (!currentFormSchema) return errors

  return errors.map((error) => {
    // For required field errors
    if (error.name === 'required' && error.params?.missingProperty) {
      const missingProp = error.params.missingProperty

      // Look up errorMessage from the stored schema
      const errorMessage = currentFormSchema.errorMessage

      if (errorMessage) {
        const customMessage =
          errorMessage.required?.[missingProp] || errorMessage.required

        if (customMessage) {
          return {
            ...error,
            message: customMessage,
            stack: customMessage,
          }
        }
      }
    }

    // For format/pattern/validation errors on properties
    if (
      error.name === 'format' ||
      error.name === 'pattern' ||
      error.name === 'minLength' ||
      error.name === 'maxLength'
    ) {
      if (
        error.name === 'format' &&
        error.params?.format === 'date-not-future-spin'
      ) {
        return {
          ...error,
          message: SPIN_CASHBACK_DATE_ERROR_MESSAGE,
          stack: SPIN_CASHBACK_DATE_ERROR_MESSAGE,
        }
      }

      const fieldName = error.property
        ? error.property.replace(/^\./, '')
        : error.params?.missingProperty

      if (fieldName && currentFormSchema?.errorMessage) {
        const customMessage =
          currentFormSchema.errorMessage.properties?.[fieldName]

        if (customMessage) {
          return {
            ...error,
            message: customMessage,
            stack: customMessage,
          }
        }
      }
    }

    return error
  })
}

// Create Form component with shadcn theme
const Form = withTheme(ShadcnTheme)

// Custom Text Widget
const CustomTextWidget = (props) => {
  // Check if this is a numeric-only field (like card last 4 digits)
  const isNumericField =
    props.schema?.pattern === '^[0-9]{4}$' ||
    props.schema?.pattern === '^[0-9]{8}$' ||
    props.id?.toLowerCase().includes('cardlast4') ||
    props.id?.toLowerCase().includes('cardfirst8')

  return (
    <Input
      id={props.id}
      type="text"
      inputMode={isNumericField ? 'numeric' : undefined}
      pattern={isNumericField ? '[0-9]*' : undefined}
      placeholder={props.placeholder}
      value={props.value || ''}
      onChange={(e) => props.onChange(e.target.value)}
      required={props.required}
      disabled={props.disabled}
      readOnly={props.readonly}
    />
  )
}

// Custom Number Widget
// const CustomNumberWidget = (props) => {
//   const handleChange = (e) => {
//     const value = e.target.value
//     // Check if it's an Amount field (by ID convention) and restrict to 7 digits
//     if (props.id && props.id.toLowerCase().includes('amount')) {
//       if (value.length > 7) {
//         return // Ignore input if length > 7
//       }
//     }

//     props.onChange(value === '' ? undefined : Number(value))
//   }

//   return (
//     <Input
//       id={props.id}
//       type="number"
//       placeholder={props.placeholder}
//       value={props.value || ''}
//       onChange={handleChange}
//       required={props.required}
//       disabled={props.disabled}
//       readOnly={props.readonly}
//     />
//   )
// }

const CustomNumberWidget = (props) => {
  const handleChange = (e) => {
    const value = e.target.value

    if (value !== '' && Number(value) < 0) {
      return
    }

    if (props.id && props.id.toLowerCase().includes('amount')) {
      if (value.length > 7) {
        return
      }
    }

    props.onChange(value === '' ? undefined : Number(value))
  }

  return (
    <Input
      id={props.id}
      type="number"
      inputMode="numeric"
      pattern="[0-9]*"
      min="0"
      placeholder={props.placeholder}
      value={props.value || ''}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
          e.preventDefault()
        }
      }}
      required={props.required}
      disabled={props.disabled}
      readOnly={props.readonly}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
    />
  )
}

// Custom Email Widget
const CustomEmailWidget = (props) => {
  return (
    <Input
      id={props.id}
      type="email"
      placeholder={props.placeholder}
      value={props.value || ''}
      onChange={(e) => props.onChange(e.target.value)}
      required={props.required}
      disabled={props.disabled}
      readOnly={props.readonly}
    />
  )
}

// Custom Select Widget
const CustomSelectWidget = (props) => {
  const { options, value, onChange, id, placeholder } = props

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder || 'Select an option'} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.enumOptions?.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

// Custom Checkbox Widget (for single checkbox)
const CustomCheckboxWidget = (props) => {
  return (
    <div className="flex items-center md:items-start gap-2">
      <Checkbox
        id={props.id}
        checked={props.value || false}
        onCheckedChange={props.onChange}
        disabled={props.disabled}
      />
      <Label htmlFor={props.id}>
        {props.label}
        {props.required && <span className="text-red-500"> *</span>}
      </Label>
    </div>
  )
}

// Custom Checkboxes Widget (for multiple checkboxes array)
const CustomCheckboxesWidget = (props) => {
  const { options, value = [], onChange, id, disabled } = props

  const handleChange = (optionValue) => {
    const newValue = Array.isArray(value) ? [...value] : []
    const index = newValue.indexOf(optionValue)

    if (index === -1) {
      newValue.push(optionValue)
    } else {
      newValue.splice(index, 1)
    }

    onChange(newValue)
  }

  return (
    <div className="flex flex-col gap-2 md:flex-row">
      {options.enumOptions?.map((option, index) => (
        <div key={option.value} className="flex items-center gap-2">
          <Checkbox
            id={`${id}-${index}`}
            checked={Array.isArray(value) && value.includes(option.value)}
            onCheckedChange={() => handleChange(option.value)}
            disabled={disabled}
          />
          <Label htmlFor={`${id}-${index}`}>{option.label}</Label>
        </div>
      ))}
    </div>
  )
}

// Custom Date Widget
const CustomDateWidget = (props) => {
  const { t } = useLanguage()

  const handleDateChange = (date) => {
    // Ensure we're passing the date in the correct format
    props.onChange(date || undefined)
  }

  // Check if the schema format is 'date-not-future' to disable future dates
  const schema = props.schema || {}
  const formContext = props.formContext || props.registry?.formContext || {}
  const isCashbackSpinConditionalForm =
    formContext.formId === 'cashbackMissingEmailReceived' ||
    formContext.formId === 'cashbackMissingEmailNotReceived'
  const isTransactionDateField = props.id?.endsWith('transactionDate')
  const requestToCheckSelections = Array.isArray(
    formContext.formData?.requestToCheck,
  )
    ? formContext.formData.requestToCheck
    : []
  const hasSpinCashbackSelected = requestToCheckSelections.some((selection) =>
    SPIN_CASHBACK_TYPE_VALUES.has(selection),
  )

  const effectiveFormat =
    isCashbackSpinConditionalForm &&
    isTransactionDateField &&
    hasSpinCashbackSelected
      ? 'date-not-future-spin'
      : schema.format

  const disableFutureDates =
    effectiveFormat === 'date-not-future' ||
    effectiveFormat === 'date-not-future-spin'
  const minDate =
    effectiveFormat === 'date-not-future-spin'
      ? SPIN_CASHBACK_MIN_DATE
      : undefined

  return (
    <CalendarDropdown
      className="w-full"
      value={props.value || ''}
      onChange={handleDateChange}
      disableFutureDates={disableFutureDates}
      minDate={minDate}
      placeholder={props.placeholder || t('inquiry.selectDate')}
    />
  )
}

// Custom File Widget with image compression
const CustomFileWidget = (props) => {
  const { t } = useLanguage()
  const [files, setFiles] = useState([])
  const [error, setError] = useState('')
  const inputRef = useRef(null)
  const isProcessingRef = useRef(false)
  const previousValueRef = useRef(props.value)

  // Determine if multiple files are allowed
  // Schema type 'array' indicates multiple files
  const isMultiple =
    props.schema?.type === 'array' || props.options?.multiple === true

  // Get max file size from schema/options (default to 10MB if not specified)
  const maxFileSizeMB = props.schema?.description?.includes('MB')
    ? parseInt(props.schema.description.match(/(\d+)MB/)?.[1] || '10')
    : props.options?.maxFileSizeMB || 10

  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024

  // Get description text from ui:options or use translated text
  const description =
    props.options?.description ||
    props.schema?.description ||
    t('inquiry.maxFileSize')

  const allowedMimeTypes = ALLOWED_INQUIRY_MIME_TYPES
  const acceptAttribute = ALLOWED_INQUIRY_MIME_TYPES.join(',')

  // Check if a value is a file object or legacy string
  const getFileInfo = (val) => {
    if (!val) return null

    if (typeof val === 'object' && val.filename) {
      return { filename: val.filename, data: val.data }
    }

    if (typeof val === 'string') {
      if (val.includes('|')) {
        const parts = val.split('|')
        if (parts.length === 2 && parts[0].startsWith('data:')) {
          return { filename: parts[1], data: parts[0] }
        }
      } else if (val.startsWith('data:')) {
        const match = val.match(/filename=([^;]+)/)
        return { filename: match ? match[1] : 'uploaded_file', data: val }
      }
    }
    return null
  }

  // Sync state with props.value
  useEffect(() => {
    // If processing, don't sync to avoid race conditions
    if (isProcessingRef.current) return

    const val = props.value
    const prevVal = previousValueRef.current
    previousValueRef.current = val

    // Helper to compare if values effectively changed
    const hasChanged = JSON.stringify(val) !== JSON.stringify(prevVal)
    if (!hasChanged && files.length > 0) return

    if (!val) {
      if (files.length > 0) setFiles([])
      return
    }

    const newFiles = []
    if (Array.isArray(val)) {
      val.forEach((v) => {
        const info = getFileInfo(v)
        if (info) newFiles.push(info)
      })
    } else {
      const info = getFileInfo(val)
      if (info) newFiles.push(info)
    }

    // Only update if different to avoid loops
    if (JSON.stringify(newFiles) !== JSON.stringify(files)) {
      setFiles(newFiles)
    }
  }, [props.value, files])

  const validateFileType = (file) => {
    if (!file || !file.name) {
      return { valid: false, error: 'Invalid file. Please try again.' }
    }

    const fileName = file.name.toLowerCase()
    const extensionMatch = fileName.match(/(\.[^./\\]+)$/)
    const fileExtension = extensionMatch ? extensionMatch[1] : ''

    if (
      fileExtension &&
      BLOCKED_EXECUTABLE_EXTENSIONS.includes(fileExtension)
    ) {
      return {
        valid: false,
        error:
          'This file type is not allowed. Please upload JPEG, PNG, GIF, WEBP, or PDF files only.',
      }
    }

    const fileType = (file.type || '').toLowerCase()
    const isValidMimeType = allowedMimeTypes.includes(fileType)

    if (!isValidMimeType) {
      return {
        valid: false,
        error: `${t('inquiry.fileTypeNotSupported')} ${allowedMimeTypes.join(', ')}`,
      }
    }

    return { valid: true }
  }

  const validateFileSize = (file) => {
    if (file.size > maxFileSizeBytes) {
      return {
        valid: false,
        error: `File ${file.name} exceeds ${maxFileSizeMB}MB limit.`,
      }
    }
    return { valid: true }
  }

  const processFile = async (file) => {
    // Validation
    const typeValid = validateFileType(file)
    if (!typeValid.valid) throw new Error(typeValid.error)

    const sizeValid = validateFileSize(file)
    if (!sizeValid.valid) throw new Error(sizeValid.error)

    return new Promise(async (resolve, reject) => {
      // Create object structure
      const createObj = (dataUrl) => ({
        data: dataUrl,
        filename: file.name,
      })

      try {
        if (file.type.startsWith('image/')) {
          // Compress images
          try {
            const compressed = await compressImage(file, 1024, 1024, 0.8)
            resolve(createObj(compressed))
          } catch (e) {
            // Fallback to original if compression fails
            const reader = new FileReader()
            reader.onload = (e) => resolve(createObj(e.target.result))
            reader.onerror = () => reject(new Error('Failed to read file'))
            reader.readAsDataURL(file)
          }
        } else {
          // Other files
          const reader = new FileReader()
          reader.onload = (e) => resolve(createObj(e.target.result))
          reader.onerror = () => reject(new Error('Failed to read file'))
          reader.readAsDataURL(file)
        }
      } catch (err) {
        reject(err)
      }
    })
  }

  // Handle new files selection
  const handleFileChange = async (fileList) => {
    setError('')
    if (!fileList || fileList.length === 0) return

    isProcessingRef.current = true
    const currentFiles = isMultiple ? [...files] : [] // If single, replace
    const processedNewFiles = []
    const errors = []

    // Process each selected file
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]

      // Skip if single mode and we already have a candidate
      if (!isMultiple && i > 0) break

      try {
        const fileObj = await processFile(file)
        processedNewFiles.push(fileObj)
      } catch (err) {
        errors.push(err.message)
      }
    }

    isProcessingRef.current = false

    if (errors.length > 0) {
      setError(errors.join(', '))
    }

    if (processedNewFiles.length > 0) {
      const updatedList = isMultiple
        ? [...currentFiles, ...processedNewFiles]
        : processedNewFiles
      setFiles(updatedList)
      propagateChange(updatedList)
    }

    // Reset input
    if (inputRef.current) inputRef.current.value = ''
  }

  // Propagate changes to parent form
  const propagateChange = (fileList) => {
    if (!props.onChange) return

    if (isMultiple) {
      props.onChange(fileList)
    } else {
      const file = fileList[0]
      if (file) {
        props.onChange(`${file.data}|${file.filename}`)
      } else {
        props.onChange(undefined)
      }
    }
  }

  const handleRemove = (index) => {
    const newFiles = [...files]
    if (isMultiple) {
      newFiles.splice(index, 1)
      setFiles(newFiles)
      propagateChange(newFiles)
    } else {
      setFiles([])
      propagateChange([])
    }
  }

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileChange(e.target.files)
    }
  }

  return (
    <div className="w-full">
      {/* File List */}
      {files.length > 0 && (
        <div className="flex flex-col gap-2 mb-3">
          {files.map((file, idx) => (
            <div
              key={idx}
              className="flex items-start justify-between gap-3 w-full rounded-lg border border-primary bg-primary/10 py-3 px-4"
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <svg
                  className="h-5 w-5 text-theme-secondary flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="text-sm font-medium text-theme-secondary break-all">
                  {file.filename}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="flex-shrink-0 text-gray-500 hover:text-red-500 transition-colors"
                aria-label="Remove file"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {(isMultiple || files.length === 0) && (
        <div
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-3 w-full rounded-lg border border-input py-4 px-6 min-h-[46px] cursor-pointer transition-colors hover:bg-theme-primary/10 hover:text-accent-foreground focus-visible:outline-none border-dashed"
        >
          <Upload className="h-5 w-5 text-muted-foreground" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-muted-foreground">
              {isMultiple
                ? props.placeholder || t('inquiry.uploadFile')
                : props.placeholder || t('inquiry.uploadFile')}
            </span>
            {description && (
              <span className="text-xs text-gray-500">{description}</span>
            )}
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        id={props.id}
        type="file"
        multiple={isMultiple}
        accept={acceptAttribute}
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Error message */}
      {error && (
        <div className="mt-2 text-sm text-red-600">
          <div className="flex items-start gap-2">
            <p id="email-error" className="text-sm text-red-500 mt-1 text-left">
              <div className="flex items-start gap-2">
                <span className="w-1 h-1 mt-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
                {error}
              </div>
            </p>
          </div>
        </div>
      )}

      {/* Sensitive information disclaimer */}
      <div className="mt-2 flex items-start gap-2">
        <svg
          className="h-4 w-4 text-blue-600 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-xs text-gray-600 text-left">
          {t('inquiry.privacyNotice')}
        </p>
      </div>
    </div>
  )
}

// Helper function to compress images
const compressImage = (file, maxWidth, maxHeight, quality) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width)
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height)
            height = maxHeight
          }
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to base64 with quality compression
        const compressedDataUrl = canvas.toDataURL(file.type, quality)
        resolve(compressedDataUrl)
      }
      img.onerror = reject
    }
    reader.onerror = reject
  })
}

// Custom Multi-Select Widget using shadcn MultiSelect
const CustomMultiSelectWidget = (props) => {
  const { t } = useLanguage()
  const { options, value = [], onChange, id, placeholder } = props
  const formContext = props.formContext || props.registry?.formContext || {}

  const isCampaignField = id?.includes('campaignName')
  const isTargetForm =
    formContext?.formId === 'cashbackMissingEmailReceived' ||
    formContext?.formId === 'cashbackMissingEmailNotReceived'
  const requestToCheck = Array.isArray(formContext?.formData?.requestToCheck)
    ? formContext.formData.requestToCheck
    : []

  const hasCashback = requestToCheck.some((selection) =>
    CASHBACK_TYPE_VALUES.has(selection),
  )
  const hasSpinCashback = requestToCheck.some((selection) =>
    SPIN_CASHBACK_TYPE_VALUES.has(selection),
  )

  const filteredOptions =
    isCampaignField && isTargetForm
      ? (options.enumOptions || []).filter((option) => {
          const optionValue = String(option.value)

          if (hasCashback && hasSpinCashback) {
            return (
              CASHBACK_ONLY_CAMPAIGN_VALUES.has(optionValue) ||
              SPIN_ONLY_CAMPAIGN_VALUES.has(optionValue)
            )
          }

          if (hasSpinCashback) {
            return SPIN_ONLY_CAMPAIGN_VALUES.has(optionValue)
          }

          if (hasCashback) {
            return CASHBACK_ONLY_CAMPAIGN_VALUES.has(optionValue)
          }

          return false
        })
      : options.enumOptions || []

  useEffect(() => {
    if (!isCampaignField || !isTargetForm) return

    const selectedValues = Array.isArray(value) ? value : []
    if (selectedValues.length === 0) return

    const allowedValues = new Set(filteredOptions.map((option) => option.value))
    const validSelections = selectedValues.filter((selectedValue) =>
      allowedValues.has(selectedValue),
    )

    if (validSelections.length !== selectedValues.length) {
      onChange(validSelections.length > 0 ? validSelections : undefined)
    }
  }, [isCampaignField, isTargetForm, value, filteredOptions, onChange])

  return (
    <MultiSelect
      values={Array.isArray(value) ? value : []}
      onValuesChange={(newValues) => {
        onChange(newValues.length > 0 ? newValues : undefined)
      }}
    >
      <MultiSelectTrigger id={id} className="w-full">
        <MultiSelectValue
          placeholder={placeholder || t('inquiry.selectCampaigns')}
          className="text-sm"
          overflowBehavior="wrap"
        />
      </MultiSelectTrigger>
      <MultiSelectContent>
        {filteredOptions.map((option) => (
          <MultiSelectItem key={option.value} value={option.value}>
            {option.label}
          </MultiSelectItem>
        ))}
      </MultiSelectContent>
    </MultiSelect>
  )
}

/**
 * Substitute template variables in a string with actual values from context
 */
const substituteTemplateVariables = (template, context) => {
  if (!template || typeof template !== 'string') return template

  const { tenant, client } = context

  return template.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (match, path) => {
    const parts = path.split('.')
    let value = null

    if (parts[0] === 'tenant' && tenant) {
      value = parts.slice(1).reduce((obj, key) => obj?.[key], tenant)
    } else if (parts[0] === 'client' && client) {
      value = parts.slice(1).reduce((obj, key) => obj?.[key], client)
    }

    return value !== null && value !== undefined ? String(value) : ''
  })
}

/**
 * Extract options from API response using valuePath and labelPath
 */
const extractOptionsFromResponse = (response, valuePath, labelPath) => {
  if (!response || !valuePath || !labelPath) return []

  try {
    // Handle root-level array (response is the array itself)
    if (Array.isArray(response) && valuePath.startsWith('[{{index}}]')) {
      const itemPath = valuePath.replace(/^\[{{index}}\]\.?/, '')
      const labelItemPath = labelPath.replace(/^\[{{index}}\]\.?/, '')

      return response
        .map((item) => {
          let value = item
          if (itemPath) {
            const valueParts = itemPath.split('.')
            value = valueParts.reduce((obj, key) => obj?.[key], item)
          }

          let label = item
          if (labelItemPath) {
            const labelParts = labelItemPath.split('.')
            label = labelParts.reduce((obj, key) => obj?.[key], item)
          } else {
            label = value
          }

          return {
            value: value !== null && value !== undefined ? String(value) : '',
            label:
              label !== null && label !== undefined
                ? String(label)
                : String(value),
          }
        })
        .filter((option) => option.value !== '')
    }

    // Check if path contains array iteration pattern with field name
    const arrayMatch = valuePath.match(/^(\w+)\[{{index}}\](.*)$/)
    if (arrayMatch) {
      const arrayKey = arrayMatch[1]
      const itemPath = arrayMatch[2].replace(/^\./, '')

      const array = response[arrayKey]
      if (!Array.isArray(array)) return []

      return array
        .map((item, index) => {
          let value = item
          if (itemPath) {
            const valueParts = itemPath.split('.')
            value = valueParts.reduce((obj, key) => obj?.[key], item)
          }

          const labelMatch = labelPath.match(/^(\w+)\[{{index}}\](.*)$/)
          let label = value
          if (labelMatch) {
            const labelItemPath = labelMatch[2].replace(/^\./, '')
            if (labelItemPath) {
              const labelParts = labelItemPath.split('.')
              label = labelParts.reduce((obj, key) => obj?.[key], item)
            } else {
              label = item
            }
          } else {
            const labelParts = labelPath.split('.')
            label = labelParts.reduce((obj, key) => obj?.[key], response)
          }

          return {
            value: value !== null && value !== undefined ? String(value) : '',
            label:
              label !== null && label !== undefined
                ? String(label)
                : String(value),
          }
        })
        .filter((option) => option.value !== '')
    } else {
      // Simple path without array iteration
      const valueParts = valuePath.split('.')
      const labelParts = labelPath.split('.')

      const value = valueParts.reduce((obj, key) => obj?.[key], response)
      const label = labelParts.reduce((obj, key) => obj?.[key], response)

      return [
        {
          value: value !== null && value !== undefined ? String(value) : '',
          label:
            label !== null && label !== undefined
              ? String(label)
              : String(value),
        },
      ].filter((option) => option.value !== '')
    }
  } catch (error) {
    logger.error(
      'Error extracting options from response:',
      sanitizeLogMessage(error),
    )
    return []
  }
}

// Custom Dynamic Select Widget for fields with dataSource configuration
const CustomDynamicSelectWidget = (props) => {
  const { schema, value, onChange, id, placeholder, disabled } = props
  const { tenant } = useTenant()
  const { client } = useClient()

  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const dataSource = schema?.dataSource

  useEffect(() => {
    if (!dataSource || dataSource.type !== 'api') {
      return
    }

    const fetchOptions = async () => {
      setLoading(true)
      setError(null)

      try {
        const url = substituteTemplateVariables(dataSource.url, {
          tenant,
          client,
        })
        const params = {}

        if (dataSource.params) {
          Object.keys(dataSource.params).forEach((key) => {
            params[key] = substituteTemplateVariables(dataSource.params[key], {
              tenant,
              client,
            })
          })
        }

        const response = await apiService.get(url, { params })
        const responseData = response.data || response

        const extractedOptions = extractOptionsFromResponse(
          responseData,
          dataSource.valuePath,
          dataSource.labelPath,
        )

        if (extractedOptions.length === 0) {
        } else {
          setOptions(extractedOptions)

          if (extractedOptions.length === 1 && !value) {
            onChange(extractedOptions[0].value)
          }
        }
      } catch (err) {
        logger.error('Error fetching dynamic options:', sanitizeLogMessage(err))
        setError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load options',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchOptions()
  }, [dataSource, tenant, client])

  if (!dataSource || dataSource.type !== 'api') {
    return <CustomSelectWidget {...props} />
  }

  const emptyPlaceholder = dataSource?.emptyPlaceholder
  const isEmpty = !loading && !error && options.length === 0 && emptyPlaceholder

  return (
    <div className="w-full">
      <Select
        value={value || ''}
        onValueChange={onChange}
        disabled={disabled || loading || isEmpty}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue
            placeholder={
              loading
                ? 'Loading...'
                : isEmpty
                  ? emptyPlaceholder
                  : placeholder || 'Select an option'
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {loading && (
              <SelectItem value="__loading__" disabled>
                Loading options...
              </SelectItem>
            )}
            {error && !loading && (
              <SelectItem value="__error__" disabled>
                {error}
              </SelectItem>
            )}
            {!loading && !error && options.length === 0 && (
              <SelectItem value="__empty__" disabled>
                No options available
              </SelectItem>
            )}
            {!loading &&
              !error &&
              options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {error && !loading && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  )
}

/**
 * Searchable, paginated select widget for large option sets.
 * Loads all options once and applies search + pagination client-side.
 */
const CustomSearchablePaginatedSelectWidget = (props) => {
  const { schema, value, onChange, id, placeholder, disabled } = props
  const { tenant } = useTenant()
  const { client } = useClient()
  const { t } = useLanguage()

  const [options, setOptions] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState(null)
  const [dropdownStyle, setDropdownStyle] = useState({})

  const PAGE_LIMIT = 20
  const dataSource = schema?.dataSource
  const searchInputRef = useRef(null)
  const listRef = useRef(null)
  const triggerRef = useRef(null)
  const initialLabelFetchedRef = useRef(false)

  const fetchOptions = useCallback(async () => {
    if (!dataSource || dataSource.type !== 'api') return []

    setLoading(true)
    setError(null)

    try {
      const url = substituteTemplateVariables(dataSource.url, {
        tenant,
        client,
      })
      const params = {
        ...(dataSource.params || {}),
      }

      // Substitute template variables in params
      const resolvedParams = {}
      Object.keys(params).forEach((key) => {
        resolvedParams[key] = substituteTemplateVariables(String(params[key]), {
          tenant,
          client,
        })
      })

      const response = await apiService.get(url, { params: resolvedParams })
      const responseData = response.data || response

      const extracted = extractOptionsFromResponse(
        responseData,
        dataSource.valuePath,
        dataSource.labelPath,
      )

      // Deduplicate by value
      const seen = new Set()
      const deduped = extracted.filter((opt) => {
        if (seen.has(opt.value)) return false
        seen.add(opt.value)
        return true
      })

      setOptions(deduped)
      setHasMore(deduped.length > PAGE_LIMIT)

      return deduped
    } catch (err) {
      logger.error('Error fetching paginated options:', sanitizeLogMessage(err))
      setError(
        err.response?.data?.message || err.message || 'Failed to load options',
      )
      return []
    } finally {
      setLoading(false)
    }
  }, [dataSource, tenant, client])

  // One-time fetch to resolve selectedLabel when value is set but label is unknown
  useEffect(() => {
    if (
      value &&
      !selectedLabel &&
      !initialLabelFetchedRef.current &&
      dataSource?.type === 'api'
    ) {
      initialLabelFetchedRef.current = true
      fetchOptions().then((extracted) => {
        const match = (extracted || []).find((opt) => opt.value === value)
        if (match) setSelectedLabel(match.label)
      })
    }
  }, [value, selectedLabel, dataSource, fetchOptions])

  // Compute dropdown position when opening
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      })

      setOptions([])
      setPage(1)
      setHasMore(false)
      setSearchQuery('')
      fetchOptions().then((extracted) => {
        if (value && !selectedLabel) {
          const match = (extracted || []).find((opt) => opt.value === value)
          if (match) setSelectedLabel(match.label)
        }
      })
    }
  }, [open])

  // Focus search input once when dropdown opens (stable ref, not affected by re-renders)
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleSearchChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    setPage(1)
  }

  const normalizedQuery = searchQuery.trim().toLowerCase()
  const filteredOptions = normalizedQuery
    ? options.filter((option) => {
        const label = String(option.label || '').toLowerCase()
        const optionValue = String(option.value || '').toLowerCase()
        return (
          label.includes(normalizedQuery) ||
          optionValue.includes(normalizedQuery)
        )
      })
    : options

  const visibleOptions = filteredOptions.slice(0, page * PAGE_LIMIT)

  useEffect(() => {
    setHasMore(visibleOptions.length < filteredOptions.length)
  }, [visibleOptions.length, filteredOptions.length])

  // Infinite scroll — reveal more filtered options when user scrolls to bottom
  const handleScroll = () => {
    const el = listRef.current
    if (!el || loadingMore || !hasMore) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      setLoadingMore(true)
      setPage((prev) => prev + 1)
      setTimeout(() => setLoadingMore(false), 100)
    }
  }

  const handleSelect = (option) => {
    onChange(option.value)
    setSelectedLabel(option.label)
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange(undefined)
    setSelectedLabel(null)
    initialLabelFetchedRef.current = false
  }

  if (!dataSource || dataSource.type !== 'api') {
    return <CustomSelectWidget {...props} />
  }

  const dropdownPanel = open
    ? ReactDOM.createPortal(
        <>
          {/* Backdrop to close on outside click — z-index lower than panel */}
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setOpen(false)}
          />
          {/* Dropdown panel */}
          <div
            className="rounded-md border border-input bg-background shadow-lg"
            style={dropdownStyle}
          >
            {/* Search input */}
            <div className="p-2 border-b border-input">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={
                  t('inquiry.searchTransactions') || 'Search transactions...'
                }
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="max-h-52 overflow-y-auto"
            >
              {loading && (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {t('inquiry.loading') || 'Loading...'}
                </div>
              )}

              {!loading && error && (
                <div className="px-3 py-4 text-sm text-red-500 text-center">
                  {error}
                </div>
              )}

              {!loading && !error && filteredOptions.length === 0 && (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {t('inquiry.noTransactionsFound') || 'No transactions found'}
                </div>
              )}

              {!loading &&
                visibleOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground ${
                      value === option.value
                        ? 'bg-accent font-medium border-l-2 border-primary'
                        : ''
                    }`}
                  >
                    {option.label}
                  </div>
                ))}

              {loadingMore && (
                <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                  {t('inquiry.loadingMore') || 'Loading more...'}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )
    : null

  return (
    <div className="relative w-full">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={
            value ? 'text-foreground truncate' : 'text-muted-foreground'
          }
        >
          {selectedLabel ||
            (value ? value : placeholder || t('inquiry.selectTransaction'))}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <span
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground cursor-pointer px-1"
            >
              ✕
            </span>
          )}
          <span className="text-muted-foreground">▾</span>
        </div>
      </button>

      {dropdownPanel}
    </div>
  )
}

// Custom Field Template
const CustomFieldTemplate = (props) => {
  const {
    id,
    classNames,
    label,
    required,
    description,
    children,
    schema,
  } = props

  const isCheckbox = schema.type === 'boolean'

  return (
    <Field className={classNames}>
      {!isCheckbox && label && (
        <FieldLabel htmlFor={id}>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </FieldLabel>
      )}
      {description && (
        <p className="text-sm text-gray-500 mb-2">{description}</p>
      )}
      {children}
    </Field>
  )
}

// Custom Object Field Template
const CustomObjectFieldTemplate = (props) => {
  const getDesktopGridClassNames = (fieldName) => {
    const fieldUiSchema = props.uiSchema?.[fieldName] || {}
    const fieldOptions = fieldUiSchema['ui:options'] || {}
    const classNames = []

    if (
      fieldOptions.desktopColumnSpan === 2 ||
      (props.formContext?.formId === 'deleteAccount' &&
        fieldName === 'campaignName')
    ) {
      classNames.push('md:col-span-2')
    }

    if (fieldOptions.desktopColumnStart === 1) {
      classNames.push('md:col-start-1')
    }

    if (fieldOptions.desktopColumnStart === 2) {
      classNames.push('md:col-start-2')
    }

    return classNames.join(' ')
  }

  const orderedProperties = CAMPAIGN_FIRST_FORM_IDS.has(
    props.formContext?.formId,
  )
    ? [...props.properties].sort((a, b) => {
        if (a.name === 'campaignName') return -1
        if (b.name === 'campaignName') return 1
        return 0
      })
    : props.properties

  return (
    <FieldSet>
      <FieldGroup className="inputs-groups-container md:!grid md:!grid-cols-2">
        {orderedProperties.map((element) => (
          <div
            key={element.name}
            className={getDesktopGridClassNames(element.name)}
          >
            {element.content}
          </div>
        ))}
      </FieldGroup>
    </FieldSet>
  )
}

// Custom Error List Template
const CustomErrorListTemplate = (props) => {
  const { errors } = props
  const { t } = useLanguage()

  if (!errors || errors.length === 0) return null

  return (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-start gap-2">
        <svg
          className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-red-500">
            {t('inquiry.fillMandatoryFields')}
          </p>
        </div>
      </div>
    </div>
  )
}

// Custom widgets object
const customWidgets = {
  TextWidget: CustomTextWidget,
  EmailWidget: CustomEmailWidget,
  NumberWidget: CustomNumberWidget,
  IntegerWidget: CustomNumberWidget,
  UpDownWidget: CustomNumberWidget,
  SelectWidget: CustomSelectWidget,
  CheckboxWidget: CustomCheckboxWidget,
  CheckboxesWidget: CustomCheckboxesWidget,
  DateWidget: CustomDateWidget,
  FileWidget: CustomFileWidget,
  multiSelect: CustomMultiSelectWidget,
  dynamicSelect: CustomDynamicSelectWidget,
  searchablePaginatedSelect: CustomSearchablePaginatedSelectWidget,
}

// Custom templates object
const customTemplates = {
  FieldTemplate: CustomFieldTemplate,
  ObjectFieldTemplate: CustomObjectFieldTemplate,
  ErrorListTemplate: CustomErrorListTemplate,
}

const InquiryForm = () => {
  const { formId, tenanturlname } = useParams()
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { isAuthenticated } = useClient()
  const { languageCode, t } = useLanguage()
  const { showToast } = useToast()
  const [formSchema, setFormSchema] = useState(null)
  const [formNotFound, setFormNotFound] = useState(false)
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const [fileValidationError, setFileValidationError] = useState(null)
  const [ticketNumber, setTicketNumber] = useState(null)

  useEffect(() => {
    const fetchFormSchema = async () => {
      if (tenant && !isCustomerInquiryEnabled(tenant)) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setFormNotFound(false)
        const response = await InquiryService.getInquiryForms(
          tenant.tenantid,
          languageCode,
        )
        const schemas = response.formSchemas || {}

        if (!schemas || typeof schemas !== 'object') {
          logger.error('Invalid schemas response:', sanitizeLogMessage(schemas))
          showToast('Invalid response from server', 'error')
          navigate(`/${tenanturlname}/inquiry`)
          return
        }

        const selectedForm = schemas[formId]
        const selectedCategory = response.inquiryCategories?.find((category) =>
          category.forms?.some((form) => form.id === formId),
        )
        const selectedCategoryForm = selectedCategory?.forms?.find(
          (form) => form.id === formId,
        )

        if (
          !selectedForm ||
          selectedForm.disabled === true ||
          !selectedCategoryForm
        ) {
          logger.error(
            selectedForm?.disabled === true
              ? 'Form disabled in schemas'
              : 'Form not found in schemas',
          )
          // Removed/disabled/unknown form ids show the branded 404 page, not an error toast
          setFormSchema(null)
          setFormNotFound(true)
          return
        }

        if (
          (selectedCategory.requireAuth || selectedCategoryForm.requireAuth) &&
          !isAuthenticated
        ) {
          navigate(`/${tenanturlname}/signin`, { replace: true })
          return
        }

        if (
          (selectedCategory.publicOnly || selectedCategoryForm.publicOnly) &&
          isAuthenticated
        ) {
          navigate(`/${tenanturlname}/inquiry`, { replace: true })
          return
        }

        let { schema, uiSchema, title, isInfoOnly } = selectedForm

        if (schema && schema.properties) {
          const autoGeneratedUiSchema = { ...(uiSchema || {}) }
          let orderedSchema = schema

          if (
            CAMPAIGN_FIRST_FORM_IDS.has(formId) &&
            schema.properties.campaignName
          ) {
            const propertyNames = Object.keys(schema.properties)
            const uiOrder = Array.isArray(autoGeneratedUiSchema['ui:order'])
              ? autoGeneratedUiSchema['ui:order'].filter(
                  (fieldName) =>
                    fieldName !== '*' && schema.properties[fieldName],
                )
              : []
            const orderedBaseNames = [
              ...uiOrder,
              ...propertyNames.filter(
                (fieldName) => !uiOrder.includes(fieldName),
              ),
            ]
            const orderedPropertyNames = [
              'campaignName',
              ...orderedBaseNames.filter(
                (fieldName) => fieldName !== 'campaignName',
              ),
            ]

            orderedSchema = {
              ...schema,
              properties: orderedPropertyNames.reduce(
                (properties, fieldName) => {
                  properties[fieldName] = schema.properties[fieldName]
                  return properties
                },
                {},
              ),
            }

            autoGeneratedUiSchema['ui:order'] = orderedPropertyNames
          }

          Object.keys(orderedSchema.properties).forEach((fieldName) => {
            const fieldSchema = orderedSchema.properties[fieldName]

            if (
              fieldSchema.isDynamicField === true &&
              fieldSchema.dataSource &&
              fieldSchema.dataSource.type === 'api'
            ) {
              if (!autoGeneratedUiSchema[fieldName]) {
                autoGeneratedUiSchema[fieldName] = {}
              }
              // Only auto-assign dynamicSelect if no widget is already explicitly set
              if (!autoGeneratedUiSchema[fieldName]['ui:widget']) {
                autoGeneratedUiSchema[fieldName]['ui:widget'] = 'dynamicSelect'
              }

              if (!autoGeneratedUiSchema[fieldName]['ui:placeholder']) {
                autoGeneratedUiSchema[fieldName]['ui:placeholder'] = `Select ${
                  fieldSchema.title || fieldName
                }`
              }
            }
          })

          schema = orderedSchema
          uiSchema = autoGeneratedUiSchema
        }

        // Store schema globally for transformErrors to access
        currentFormSchema = schema

        setFormSchema({ schema, uiSchema, title, isInfoOnly })
      } catch (error) {
        logger.error('Error fetching form schema:', sanitizeLogMessage(error))
        showToast('Failed to load form', 'error')
      } finally {
        setLoading(false)
      }
    }

    if (tenant?.tenantid && formId) {
      fetchFormSchema()
    }
  }, [
    tenant?.tenantid,
    tenant?.tenantmasterconfig?.customerquery?.show,
    formId,
    isAuthenticated,
    languageCode,
  ])

  const isSecurityFileValidationError = (code, rawMessage = '') => {
    const normalizedMessage = String(rawMessage || '').toLowerCase()

    if (code === 'CQER0008') {
      return true
    }

    return (
      normalizedMessage.includes('binary signature') ||
      normalizedMessage.includes('unsupported file mime type') ||
      normalizedMessage.includes('blocked executable file extension') ||
      normalizedMessage.includes('invalid attachment type') ||
      normalizedMessage.includes('invalid attachment signature') ||
      normalizedMessage.includes('unable to detect file type')
    )
  }

  const showFileValidationError = (message) => {
    setFileValidationError(message)
    setTimeout(() => {
      const fileInput = document.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  const handleSubmit = async ({ formData: submittedData }) => {
    try {
      setShowLoader(true)

      const transformedData = { ...submittedData }
      const submissionMetadata = buildInquirySubmissionMetadata(
        formId,
        submittedData,
        formSchema?.schema,
      )

      Object.keys(transformedData).forEach((key) => {
        const value = transformedData[key]
        if (typeof value === 'string' && value.includes('|')) {
          const parts = value.split('|')
          if (parts.length === 2 && parts[0].startsWith('data:')) {
            transformedData[key] = {
              data: parts[0],
              filename: parts[1],
            }
          }
        }
      })

      const response = await InquiryService.submitInquiry(
        tenant.tenantid,
        formId,
        transformedData,
        languageCode,
        submissionMetadata,
      )

      const result = response.data || response

      if (result.success) {
        setTicketNumber(result.ticketKey)
        setShowLoader(false)
        setShowSuccessModal(true)
      } else {
        setShowLoader(false)

        const resultCode = result?.code
        const resultErrorDetails = result?.error || result?.errorDetails || ''

        if (isSecurityFileValidationError(resultCode, resultErrorDetails)) {
          showFileValidationError(t('inquiry.fileValidationSecurityFailed'))
        } else {
          setFileValidationError(null)
        }
      }
    } catch (error) {
      logger.error('Error submitting inquiry:', sanitizeLogMessage(error))
      setShowLoader(false)

      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        t('inquiry.errorSubmittingInquiry')

      const errorCode = error.response?.data?.code || error.data?.code
      const errorDetails =
        error.response?.data?.error ||
        error.response?.data?.errorDetails ||
        error.data?.error ||
        error.data?.errorDetails ||
        errorMessage

      if (isSecurityFileValidationError(errorCode, errorDetails)) {
        if (errorCode === 'CQER0008') {
          showFileValidationError(
            errorMessage || t('inquiry.fileValidationFailed'),
          )
        } else {
          showFileValidationError(t('inquiry.fileValidationSecurityFailed'))
        }
      } else {
        setFileValidationError(null)
      }
    }
  }

  const handleModalClose = () => {
    setShowSuccessModal(false)
    setTicketNumber(null)
    navigate(`/${tenanturlname}/inquiry`)
  }

  const handleChange = ({ formData: newFormData }) => {
    setFormData(newFormData)
    if (fileValidationError) {
      setFileValidationError(null)
    }
  }

  if (loading) {
    return (
      <div className="inquiry-form-page">
        <div className="flex flex-col items-center mx-auto mt-8 px-4 md:px-0">
          <div className="w-full flex items-center mb-6">
            <a
              onClick={() => navigate(`/${tenanturlname}/inquiry`)}
              className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
            >
              <BackIcon className="h-5 w-5 text-theme-primary" />
            </a>
            <h1 className="text-base font-extrabold text-theme-secondary">
              {t('inquiry.loading')}
            </h1>
          </div>
        </div>
      </div>
    )
  }

  if ((tenant && !isCustomerInquiryEnabled(tenant)) || formNotFound) {
    return <NotFound />
  }

  if (!formSchema) {
    return (
      <div className="inquiry-form-page">
        <div className="flex flex-col items-center mx-auto mt-8 px-4 md:px-0">
          <div className="w-full flex items-center mb-6">
            <a
              onClick={() => navigate(`/${tenanturlname}/inquiry`)}
              className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
            >
              <BackIcon className="h-5 w-5 text-theme-primary" />
            </a>
            <h1 className="text-base font-extrabold text-theme-secondary">
              {t('inquiry.formNotFound')}
            </h1>
          </div>
        </div>
      </div>
    )
  }

  if (formSchema?.isInfoOnly) {
    const phoneNotice = formSchema.schema?.properties?.phoneNotice?.const || ''
    const infoHtml = formSchema.schema?.properties?.infoHtml?.const || ''
    const infoText = formSchema.schema?.properties?.info?.const || ''
    const normalizedInfoHtml = [
      normalizeInquiryInfoHtml(infoHtml || infoText, formId),
      phoneNotice
        ? `<p><strong>${escapeInquiryHtml(phoneNotice)}</strong></p>`
        : '',
    ]
      .filter(Boolean)
      .join('')

    return (
      <div className="inquiry-form-page">
        <div className="flex flex-col items-center mx-auto mt-8 px-4">
          <div className="w-full flex items-center mb-6">
            <a
              onClick={() => navigate(`/${tenanturlname}/inquiry`)}
              className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
            >
              <BackIcon className="h-5 w-5 text-theme-primary" />
            </a>
            <h1 className="text-base font-extrabold text-theme-secondary">
              {formSchema.title || t('inquiry.information')}
            </h1>
          </div>

          <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center">
            <div
              className="inquiry-info-html text-sm text-gray-700 text-center leading-7"
              dangerouslySetInnerHTML={{
                __html: Sanitizer.sanitizeHTML(normalizedInfoHtml),
              }}
            />
          </div>

          <div className="w-full mt-6">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate(`/${tenanturlname}/inquiry`)}
              className="mt-6 md:w-[400px]"
            >
              {t('inquiry.backToInquiryMenu')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="inquiry-form-page">
      <div className="flex flex-col items-center mx-auto mt-8 px-4 md:px-0">
        <div className="w-full flex items-center mb-6">
          <a
            onClick={() => navigate(`/${tenanturlname}/inquiry`)}
            className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
          >
            <BackIcon className="h-5 w-5 text-theme-primary" />
          </a>
          <h1 className="text-base font-extrabold text-theme-secondary">
            {formSchema.title || t('inquiry.inquiryForm')}
          </h1>
        </div>

        <div className="w-full mb-4">
          {fileValidationError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start gap-2">
                <svg
                  className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1 justify-items-start">
                  <p className="text-sm font-medium text-red-800">
                    {t('inquiry.fileUploadError')}
                  </p>
                  <p className="text-sm text-red-500 mt-1">
                    {fileValidationError}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Form
            schema={formSchema.schema}
            uiSchema={formSchema.uiSchema}
            formData={formData}
            formContext={{ formId, formData }}
            validator={customValidator}
            onChange={handleChange}
            onSubmit={handleSubmit}
            widgets={customWidgets}
            templates={customTemplates}
            transformErrors={transformErrors}
            className="inquiry-form-wrapper"
          >
            <Button
              id="submit-button"
              variant="primary"
              size="lg"
              type="submit"
              disabled={submitting}
              className="mt-6 md:w-[400px]"
            >
              {submitting
                ? t('inquiry.submitting')
                : t('inquiry.submitInquiry')}
            </Button>
          </Form>
        </div>
      </div>

      {showLoader && (
        <Loader fullscreen label={t('inquiry.submitting')} size="xl" />
      )}

      <Modal
        open={showSuccessModal}
        onClose={handleModalClose}
        type="success"
        title={t('inquiry.inquiryReceived')}
        description={
          <>
            {t('inquiry.supportTeamContact')}
            <br /> {t('inquiry.supportTeamContact2')}
            {ticketNumber && (
              <>
                <br />
                <br />
                <strong>
                  {t('inquiry.supportReference')} {ticketNumber}
                </strong>
              </>
            )}
            <br /> {t('inquiry.supportTeamContact3')}
          </>
        }
        showHeaderImage={false}
        buttonText={t('inquiry.ok')}
      />
    </div>
  )
}

export default InquiryForm
